'use client';

import { useState, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { DISPLAY } from './ui';

/**
 * Email subscriptions, drawn the profile's way: the channel switch's disc,
 * a row of the lists someone gets as small art, the big channel card and
 * the wall of list tiles. Pure drawing; the caller owns the data and saves.
 */

/** One Woodside email list (MP Publication), as the profile draws it. */
export interface Publication {
  publicationId: number;
  title: string;
  description: string | null;
  imageUrl: string | null;
  /** The campus newsletter: drawn with the campus's art when it has none */
  isCampus: boolean;
  subscribed: boolean;
}

/** The channel's icon as a small disc, lit while the channel is on. */
export function ChannelDot({ icon, on }: { icon: ReactNode; on: boolean }) {
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
        on ? 'bg-[#1c2b39] text-[#4cb8ab]' : 'bg-gray-100 text-gray-400'
      }`}
    >
      {icon}
    </span>
  );
}

/** The emails the person gets, as a row of their art: the first few, then "+N". Tap to change them. */
export function SubscribedPreview({
  publications,
  campusArt,
  onOpen,
  label = 'Choose which emails you get',
}: {
  publications: Publication[];
  /** Shown for the campus newsletter when it has no art of its own */
  campusArt?: ReactNode;
  onOpen: () => void;
  label?: string;
}) {
  const shown = publications.slice(0, 4);
  const more = publications.length - shown.length;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="-mx-1 flex items-center gap-1.5 overflow-hidden rounded-2xl px-1 py-1 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      {publications.length === 0 ? (
        <span className="px-1 text-xs font-semibold text-[#299c8f]">
          Not on any email lists yet. Pick some.
        </span>
      ) : (
        <>
          {shown.map((pub) => (
            <PublicationThumb key={pub.publicationId} pub={pub} campusArt={campusArt} />
          ))}
          {more > 0 && (
            <span className="flex h-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 px-2.5 text-xs font-bold text-[#1c2b39]">
              +{more}
            </span>
          )}
        </>
      )}
    </button>
  );
}

/**
 * A publication's art, small: the same fallbacks as the tile. `compact` is a
 * square the size of a ChannelDot, for a list row beside the channel switch.
 */
export function PublicationThumb({
  pub,
  campusArt,
  compact = false,
}: {
  pub: Publication;
  campusArt?: ReactNode;
  compact?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  return (
    <span
      title={pub.title}
      className={`relative block shrink-0 overflow-hidden bg-gray-100 ring-1 ring-gray-200 ${
        compact ? 'h-9 w-9 rounded-lg' : 'h-11 w-[4.9rem] rounded-xl'
      }`}
    >
      {pub.imageUrl && !broken ? (
        <img
          src={pub.imageUrl}
          alt={pub.title}
          className="h-full w-full object-cover"
          loading="lazy"
          draggable={false}
          onError={() => setBroken(true)}
        />
      ) : pub.isCampus ? (
        <span className="flex h-full w-full items-center justify-center bg-gray-50">
          {campusArt}
        </span>
      ) : (
        <span
          className={`flex h-full w-full items-center justify-center ${DISPLAY} ${compact ? 'text-base' : 'text-xl'} text-white`}
          style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #2d4a5c 100%)' }}
        >
          {pub.title.trim().charAt(0).toUpperCase()}
        </span>
      )}
    </span>
  );
}

/**
 * A channel as one big switch: navy with a teal glow while it is on, quiet
 * white while it is off. The whole card toggles.
 */
export function ChannelCard({
  icon,
  title,
  description,
  on,
  saving,
  onToggle,
}: {
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  on: boolean;
  saving: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={title}
      onClick={() => onToggle(!on)}
      className={`relative flex items-start gap-4 overflow-hidden rounded-3xl p-5 text-left transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.99] ${
        on
          ? 'bg-[#1c2b39] text-white shadow-sm'
          : 'bg-white text-[#1c2b39] shadow-sm ring-1 ring-gray-200'
      }`}
    >
      {on && (
        <span
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full opacity-60 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #299c8f, transparent)' }}
        />
      )}
      <span
        className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${on ? 'bg-[#299c8f] text-white' : 'bg-gray-100 text-gray-400'}`}
      >
        {icon}
      </span>
      <span className="relative min-w-0 flex-1">
        <span className={`${DISPLAY} block text-3xl leading-none`}>{title}</span>
        {description && (
          <span className={`mt-1.5 block text-sm ${on ? 'text-white/75' : 'text-gray-500'}`}>
            {description}
          </span>
        )}
        <span
          className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase ${on ? 'text-[#4cb8ab]' : 'text-gray-400'}`}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={3} /> : null}
          {on ? 'On' : 'Off'}
        </span>
      </span>
      <span
        aria-hidden
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-[#299c8f]' : 'bg-gray-300'}`}
      >
        <span
          className={`block h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
        />
      </span>
    </button>
  );
}

/** A publication as a tile: its art on top, the title, and a check when you get it. The whole tile toggles. */
export function PublicationTile({
  pub,
  campusArt,
  saving,
  onToggle,
}: {
  pub: Publication;
  /** Shown for the campus newsletter when it has no art of its own */
  campusArt?: ReactNode;
  saving: boolean;
  onToggle: (next: boolean) => void;
}) {
  const [broken, setBroken] = useState(false);
  const on = pub.subscribed;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={pub.title}
      onClick={() => onToggle(!on)}
      title={pub.description ?? undefined}
      className={`group relative flex flex-col overflow-hidden rounded-3xl text-left transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.99] ${
        on ? 'bg-white shadow-sm ring-2 ring-[#299c8f]' : 'bg-white shadow-sm ring-1 ring-gray-200'
      }`}
    >
      <span className="relative block aspect-[16/9] w-full overflow-hidden bg-gray-100">
        {pub.imageUrl && !broken ? (
          <img
            src={pub.imageUrl}
            alt=""
            className={`h-full w-full object-cover transition-all ${on ? '' : 'opacity-80 grayscale-[0.6]'}`}
            loading="lazy"
            draggable={false}
            onError={() => setBroken(true)}
          />
        ) : pub.isCampus ? (
          <span className="flex h-full w-full items-center justify-center bg-gray-50">
            {campusArt}
          </span>
        ) : (
          <span
            className={`flex h-full w-full items-center justify-center ${DISPLAY} text-5xl text-white`}
            style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #2d4a5c 100%)' }}
          >
            {pub.title.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <span
          aria-hidden
          className={`absolute top-2.5 right-2.5 flex h-7 w-7 items-center justify-center rounded-full shadow-sm transition-all ${
            on ? 'bg-[#299c8f] text-white' : 'bg-white/90 text-transparent ring-1 ring-gray-200'
          }`}
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-current" strokeWidth={3} />
          ) : (
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          )}
        </span>
      </span>
      <span className="flex min-h-[3.25rem] flex-col justify-center px-3.5 py-2.5">
        <span className="line-clamp-1 text-sm font-bold text-[#1c2b39]">{pub.title}</span>
        {pub.description && (
          <span className="line-clamp-1 text-xs text-gray-500">{pub.description}</span>
        )}
      </span>
    </button>
  );
}
